import {
    type APIProductCategory,
    type APIProductManufacturer,
    type APIProductModel,
} from '@visorpro/client'
import { useEffect, useState } from 'react'
import { type ParseResult } from 'papaparse'
import {
    type ProductModelMatching,
    type ColumnMapping,
    type CREATE_NEW_MODEL,
    type IGNORE_MODEL,
    type DocumentUpdate,
} from '../../types'
import { CsvColumnsDialog } from './csv-columns-dialog'
import { ModelsDialog } from './models-dialog'
import { DocumentsDialog } from './documents-dialog'

export interface MetadataDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    csv: ParseResult<unknown> | null
    manufactures: APIProductManufacturer[]
    categories: APIProductCategory[]
    modelMatchings: ProductModelMatching[]
    documentUpdates: DocumentUpdate[]
    isProcessing: boolean
    matchMode: 'sha512_hash' | 'original_filename'
    onMatchModeChange: (mode: 'sha512_hash' | 'original_filename') => void
    generateProductModelMatchings: () => Promise<void>
    setColumnMapping: (
        columnMapping: ColumnMapping,
        manufacturerId: string,
        categoryId: string,
    ) => Promise<void>
    changeModelMatching: (
        modelName: string,
        selected:
            | Fuzzysort.KeyResult<APIProductModel>
            | typeof CREATE_NEW_MODEL
            | typeof IGNORE_MODEL,
    ) => void
    submitModelMatching: () => Promise<void>
    generateDocumentUpdates: () => Promise<void>
    updateDocuments: () => Promise<void>
}

export type MetadataUploadView = 'csv' | 'models' | 'documents'

export const MetadataDialog = ({
    open,
    setOpen,
    csv,
    manufactures,
    categories,
    modelMatchings,
    documentUpdates,
    isProcessing,
    matchMode,
    onMatchModeChange,
    generateProductModelMatchings,
    setColumnMapping,
    changeModelMatching,
    submitModelMatching,
    generateDocumentUpdates,
    updateDocuments,
}: MetadataDialogProps) => {
    const [view, setView] = useState<MetadataUploadView>('csv')
    const csvColumns = (csv?.meta?.fields as string[]) ?? []

    useEffect(() => {
        if (open) setView('csv')
    }, [open])

    const onCsvColumnsDialogClose = () => setOpen(false)
    const onCsvColumnsDialogConfirm = async (
        columnMapping: ColumnMapping,
        manufacturerId: string,
        categoryId: string,
    ) => {
        await setColumnMapping(columnMapping, manufacturerId, categoryId)
        await generateProductModelMatchings()
        setView('models')
    }

    const onModelsDialogClose = () => setOpen(false)
    const onModelsDialogBack = () => setView('csv')
    const onModelsDialogConfirm = async () => {
        await submitModelMatching()
        await generateDocumentUpdates()
        setView('documents')
    }

    const onDocumentsDialogClose = () => setOpen(false)
    const onDocumentsDialogBack = async () => {
        await generateProductModelMatchings()
        setView('models')
    }
    const onDocumentsDialogConfirm = async () => {
        await updateDocuments()
        setOpen(false)
    }

    return (
        <>
            <CsvColumnsDialog
                open={open && view === 'csv'}
                csvColumns={csvColumns}
                manufactures={manufactures}
                categories={categories}
                isProcessing={isProcessing}
                onConfirm={onCsvColumnsDialogConfirm}
                onClose={onCsvColumnsDialogClose}
            />
            <ModelsDialog
                open={open && view === 'models'}
                modelMatchings={modelMatchings}
                isProcessing={isProcessing}
                onConfirm={onModelsDialogConfirm}
                onClose={onModelsDialogClose}
                onBack={onModelsDialogBack}
                changeModelMatching={changeModelMatching}
            />
            <DocumentsDialog
                open={open && view === 'documents'}
                documentUpdates={documentUpdates}
                isProcessing={isProcessing}
                matchMode={matchMode}
                onMatchModeChange={onMatchModeChange}
                onConfirm={onDocumentsDialogConfirm}
                onClose={onDocumentsDialogClose}
                onBack={onDocumentsDialogBack}
            />
        </>
    )
}
